import { IAppState } from '../context'
import {
  CestaDiscount,
  CestaOrigin,
  CestaState,
  key,
  NULL_DISCOUNT,
  Product,
  Promocion,
  Service,
} from '../reducers/cestaReducer'
import {
  ITV_APPLUS_ID,
  MONTAJE_TALLER_BATERIA_ID,
  MONTAJE_TALLER_ID,
  RECOGIDA_TALLER_ACEITE_ID,
  RECOGIDA_TALLER_ID,
} from '../sagas/cestaSagas'
import { CategoriaServicio } from '../../types/CategoriaServicio'
import { calculateImporte } from '../../utils/cestaLogic'
import {
  isAmbiguosVehicle,
  isCestaAC1,
  isCestaAC2,
  isCestaAccesoriosTransporte,
  isCestaAccesoriosTransporteMoto,
  isCestaAceite,
  isCestaBateria,
  isCestaCoche,
  isCestaMoto,
  isCestaRevisionMoto,
} from '../../../shared/cesta/utils'

const ID_NAVISION_SEGURO_NEUMATICO = 'SEGURO_NEUMATICO'

/** Steps.OPCIONES (1): abans no mostrem total ni línies de serveis del funnel (montatge, recollida…). */
export const CESTA_STEP_OPCIONES = 1

export function shouldIncludeFunnelServicesInTotal(cestaState: CestaState): boolean {
  return cestaState.step >= CESTA_STEP_OPCIONES
}

export const isPromocionForServicios = (promocion: Promocion) => {
  if (!promocion?.tipo) return false

  return (
    ['seguro', 'alineacion', 'nitrogeno', 'packinvierno'].includes(
      promocion.tipo
    ) ||
    promocion.tipo.startsWith('montaje') ||
    promocion.tipo.startsWith('kitur') ||
    promocion.tipo.startsWith('kit4x4') ||
    promocion.tipo.startsWith('kitcta')
  )
}

export const isPromocionForProductos = (promocion: Promocion) =>
  !isPromocionForServicios(promocion)

export const isPromocionForKits = (promocion: Promocion) => {
  if (!promocion?.tipo) return false

  return (
    promocion.tipo.startsWith('kitur') ||
    promocion.tipo.startsWith('kit4x4') ||
    promocion.tipo.startsWith('kitcta')
  )
}

/** Service promo on product: new API uses promocionServicio; legacy used promocion with service tipo. */
export const getServicePromocion = (product: Product): Promocion | null => {
  if (product.promocionServicio?.id_site_promocion_cesta) {
    return product.promocionServicio
  }
  if (
    product.promocion?.id_site_promocion_cesta &&
    isPromocionForServicios(product.promocion)
  ) {
    return product.promocion
  }
  return null
}

/** Product promo on product: when promocionServicio exists, promocion is always the product promo. */
export const getProductPromocion = (product: Product): Promocion | null => {
  if (!product.promocion?.id_site_promocion_cesta) return null
  if (product.promocionServicio?.id_site_promocion_cesta) {
    return product.promocion
  }
  if (isPromocionForProductos(product.promocion)) {
    return product.promocion
  }
  return null
}

export const hasActiveProductPromocion = (product: Product) => {
  const promo = getProductPromocion(product)
  return Boolean(promo?.id_site_promocion_cesta && promo.importePromocion > 0)
}

export const hasActiveKitPromocion = (product: Product) => {
  const promo = getServicePromocion(product)
  return Boolean(
    promo?.id_site_promocion_cesta &&
      promo.importePromocion > 0 &&
      isPromocionForKits(promo) &&
      product.servicios.some(
        (servicio) =>
          Boolean(servicio.categoriaServicio) &&
          promo.tipo.startsWith(servicio.categoriaServicio)
      )
  )
}

export const getMontajePromocionImporte = (products: Product[]) =>
  products.reduce((sum, product) => {
    const promo = getServicePromocion(product)
    if (
      promo &&
      (promo.tipo === 'montaje' || promo.tipo === 'montaje-porcentaje')
    ) {
      return sum + Number(promo.importePromocion)
    }
    return sum
  }, 0)


// given a set of products, this function will provide the service
// promotion if there is and applies (client specified that only those
// cestas with the promoted brand can access this kind of promotion)
export const findApplyingServicePromotion = (products: Product[]) => {
  const productWithervicePromotion = products.find((product) => {
    const servicePromo = getServicePromocion(product)
    return (
      servicePromo &&
      isPromocionForServicios(servicePromo) &&
      !isPromocionForKits(servicePromo)
    )
  })
  if (!productWithervicePromotion) return NULL_DISCOUNT

  const servicePromotion = getServicePromocion(productWithervicePromotion)!

  // the next condition forces that if there are more than one
  // brand of neumáticos in this cesta, we will not apply the promotion,
  // by client petition
  const hasMoreBrandsInCart = products.some(
    (p) =>
      p.tipo_producto === 'neumatico' &&
      p.marca !== productWithervicePromotion.marca
  )
  return hasMoreBrandsInCart ? NULL_DISCOUNT : servicePromotion
}

export const itIsDiscountedService = (
  item: Service,
  promotion: Promocion | CestaDiscount
) => {
  return (
    (item.categoriaServicio === CategoriaServicio.Nitrogeno &&
      promotion.tipo === 'nitrogeno') ||
    (item.categoriaServicio === CategoriaServicio.Seguro &&
      promotion.tipo === 'seguro') ||
    (item.categoriaServicio === CategoriaServicio.PackInvierno &&
      promotion.tipo === 'packinvierno') ||
    (item.categoriaServicio === CategoriaServicio.Alineacion &&
      promotion.tipo === 'alineacion') ||
    (item.categoriaServicio === CategoriaServicio.Kitur &&
      promotion.tipo === 'kitur') ||
    (item.categoriaServicio === CategoriaServicio.Kit4x4 &&
      promotion.tipo === 'kit4x4') ||
    (item.categoriaServicio === CategoriaServicio.Kitcta &&
      promotion.tipo === 'kitcta')
  )
}

export function hasMontajeDiscount(products: Product[]) {
  return (
    products.some((product) => {
      const servicePromo = getServicePromocion(product)
      return (
        servicePromo &&
        (servicePromo.tipo === 'montaje' ||
          servicePromo.tipo === 'montaje-porcentaje')
      )
    }) ||
    (products.some((product) => product.tipo_producto === 'neumatico') &&
      products.some((product) =>
        product.servicios.find(
          (service) =>
            service.categoriaServicio === CategoriaServicio.Montaje &&
            parseFloat(service.descuento) > 0
        )
      ))
  )
}

export const selectCesta = (appState: IAppState) => appState[key]

export const selectNumeroNeumaticos = (cestaState: CestaState) =>
  cestaState.products
    .filter((prod) => prod.tipo_producto === 'neumatico')
    .reduce((acc, p) => acc + Number(p.cantidad), 0)

export const selectNumeroProductos = (cestaState: CestaState) =>
  cestaState.products.reduce((acc, p) => acc + Number(p.cantidad), 0)

// Verifica si hay servicios en la cesta y no todos son accesorios de seguridad o transporte
// En tal caso, se seleccionara el servicio de recogida en taller y no es opcional
export const hasServices = (cestaState: CestaState) =>
  (cestaState.services.length > 0 &&
    !cestaState.services.every((s) =>
      [
        'acc-seguridad',
        'acc-portabici',
        'acc-portaesqui',
        'acc-cofre',
        'acc-otros',
        'accm-maleta',
        'accm-respaldo',
        'accm-bolsa',
      ].includes(s.tipoProductoAsociado)
    )) 
    || (cestaState.services.length > 0 && isCestaAccesoriosTransporte(cestaState.products))
    || (cestaState.services.length > 0 && isCestaAccesoriosTransporteMoto(cestaState.products))

export const needsDates = (cestaState: CestaState) =>
  cestaState.products.filter((prod) => prod.tipo_producto === 'neumatico')
    .length > 0

export const needsDatesExtends = (cestaState: CestaState) => {
  if (cestaState.products.length === 1) {
    if (cestaState.products[0].tipo_producto === 'neumatico') {
      return 'neum'
    } else if (cestaState.products[0].tipo_producto === 'revision') {
      return 'rev'
    } else {
      return 'prod'
    }
  } else if (cestaState.products.length > 1) {
    const neumatico = cestaState.products.find(
      (prod) => prod.tipo_producto === 'neumatico'
    )
    const revision = cestaState.products.find(
      (prod) => prod.tipo_producto === 'revision'
    )
    const otro = cestaState.products.find(
      (prod) =>
        prod.tipo_producto !== 'neumatico' && prod.tipo_producto !== 'revision'
    )
    if (neumatico && revision && otro) {
      return 'prod_rev_neum'
    } else if (neumatico && revision) {
      return 'combi'
    } else if (revision && otro) {
      return 'prod_rev'
    } else if (neumatico && otro) {
      return 'prod_neum'
    } else {
      return 'neum'
    }
  }
  return 'neum'
}

/***

Cálculo de la cesta:
   Los importes de los productos y servicios se toman con el IVA incluido (precios totales).
   Las promociones que afectan a productos o servicios se descuentan directamente sobre esos importes con IVA (ya calculados y redondeados).
   Si la promoción es del tipo 'montaje' (o similar), se descuenta el importe de la promoción con IVA, directamente del precio con IVA del servicio de montaje.
   Las promociones en porcentaje se calculan y aplican sobre el importe con IVA.
   Finalmente, el total se suma y se redondea una única vez después de aplicar todas las promociones e impuestos.

 ***/
export const selectImporteTotalProductos = (cestaState: CestaState) => {
  // Mateix criteri que API reCalcCestaProducts: suma imports amb IVA i resta
  // importePromocion amb IVA (evita ±0,01€ en passar a base sense IVA).
  let total = 0.0
  cestaState.products.forEach((p) => {
    const productPromo = getProductPromocion(p)
    const servicePromo = getServicePromocion(p)

    total += Number(p.importe) || 0
    if (productPromo?.importePromocion) {
      total -= Number(productPromo.importePromocion) || 0
    }

    p.servicios.forEach((s) => {
      // Moto/funnel montaje va a MONTAJE_TALLER, no al total de productes
      if (s.categoriaServicio === CategoriaServicio.Montaje) {
        return
      }
      total += Number(s.importe) || 0
    })

    // Kitur/kit4x4/kitcta: una sola resta per producte (com API)
    if (
      servicePromo &&
      isPromocionForKits(servicePromo) &&
      servicePromo.importePromocion &&
      p.servicios.some(
        (s) =>
          Boolean(s.categoriaServicio) &&
          servicePromo.tipo.startsWith(s.categoriaServicio)
      )
    ) {
      total -= Number(servicePromo.importePromocion) || 0
    }
  })
  return Math.round(total * 100) / 100
}

/**
 * Serveis funnel MONTAJE_TALLER / MONTAJE_TALLER_BATERIA: un sol IVA sobre la base.
 * L'API pot enviar base a `importeSinImpuesto` i brut a `importe`, o només `importe` amb IVA.
 * Si `importeSinImpuesto` ja ve igual (≈) a `importe`, és un import amb IVA mal etiquetat: no el multipliquem per (1+IVA).
 */
export function isFunnelMontajeTallerService(service: {
  id_navision: string
}): boolean {
  return (
    service.id_navision === MONTAJE_TALLER_BATERIA_ID ||
    service.id_navision === MONTAJE_TALLER_ID
  )
}

export function importeFunnelMontajeAmbIva(service: Service): number {
  const ivaPct = parseFloat(String(service.impuesto ?? '21')) || 0
  const factor = 1 + ivaPct / 100

  const importeNum = Number(service.importe)
  const sinRaw = service.importeSinImpuesto
  const sinNum = sinRaw == null ? NaN : Number(sinRaw)
  const hasValidSin = !Number.isNaN(sinNum)

  const centsEq = (a: number, b: number) =>
    Math.round(Math.abs(a - b) * 100) / 100 <= 0.02

  if (hasValidSin && !Number.isNaN(sinNum)) {
    const grossFromBase = Math.round(sinNum * factor * 100) / 100
    if (!Number.isNaN(importeNum)) {
      if (centsEq(importeNum, grossFromBase)) {
        return Math.round(importeNum * 100) / 100
      }
      if (centsEq(importeNum, sinNum)) {
        return Math.round(importeNum * 100) / 100
      }
    }
    return grossFromBase
  }

  if (!Number.isNaN(importeNum)) {
    return Math.round(importeNum * 100) / 100
  }
  return 0
}

// TODO: This function will now have to check if there is
// an available service discount
export const selectImporteTotalServicios = (cestaState: CestaState) => {
  const servicePromotion = findApplyingServicePromotion(cestaState.products)
  const selectedServices = cestaState.services.filter((s) => s.selected)

  let totals = 0
  selectedServices.forEach((service) => {
    const importeServicio = isFunnelMontajeTallerService(service)
        ? importeFunnelMontajeAmbIva(service)
        : service.importe

    if (itIsDiscountedService(service, servicePromotion)) {
      totals +=
        importeServicio -
        (importeServicio * servicePromotion.importe_cupon) / 100
    } else {
      totals += importeServicio
    }
  })
  return totals
}

const calculateGlobalDiscount = (
  products: Product[],
  importe_cesta: number,
  tipo: string | null,
  importe_cupon: number,
  tipo_producto: string
) => {
  const matches = tipo_producto
    ? products.some((p) => p.tipo_producto === tipo_producto)
    : true

  if (!matches) {
    return 0
  }

  switch (tipo) {
    case 'global-absoluto':
      const discountAbsolute = Math.round(100 * Number(importe_cupon)) / 100
      return discountAbsolute >= importe_cesta ? 0 : discountAbsolute
    case 'global-porcentaje':
      const discountPercent = Math.round(((100 * Number(importe_cupon)) / 100) * importe_cesta) / 100
      return discountPercent >= importe_cesta ? 0 : discountPercent
    default:
      return 0
  }
}

export const selectGlobalDiscount = (cestaState: CestaState) => {
  return cestaState.discount
}

// TODO: En la página Tu Solicitud hay que calcular el descuento global sólo sobre los productos

export const selectGlobalDiscountAmount = (cestaState: CestaState) => {
  const globalDiscount = selectGlobalDiscount(cestaState)
  const importe_cesta =
    selectImporteTotalProductos(cestaState) +
    selectImporteTotalServicios(cestaState)

  return calculateGlobalDiscount(
    cestaState.products,
    importe_cesta,
    globalDiscount.tipo,
    globalDiscount.importe_cupon,
    globalDiscount.tipo_producto
  )
}

const selectMontajeMotoDiscount = (cestaState: CestaState) => {
  const service = cestaState.services.find(
    (s) => s.id_navision === MONTAJE_TALLER_ID && s.selected
  )
  if (!service) {
    return 0
  }
  const fromService = Number(service.promocion?.importePromocion) || 0
  if (fromService > 0) {
    return fromService
  }
  // Fallback: promo a promocionServicio del producte (abans que l'API ompli service.promocion)
  return getMontajePromocionImporte(cestaState.products)
}
export const selectImporteTotal = (cestaState: CestaState) => {
  const includeFunnelServices = shouldIncludeFunnelServicesInTotal(cestaState)

  // /cesta/solicitud (step < OPCIONES): no mostrar muntatge/recollida del funnel
  // encara que l'API els torni selected. Calcular productes amb promos kitur/producte
  // (totals.totalProductos és brut i no porta el descompte; totals.total ja suma el muntatge).
  if (!includeFunnelServices) {
    const total =
      selectImporteTotalProductos(cestaState) -
      selectGlobalDiscountAmount(cestaState)
    return Math.round(total * 100) / 100
  }

  // Des d'opcions: preferir totals.total de l'API (kitur + serveis funnel).
  if (cestaState.totals != null) {
    return Math.round(cestaState.totals.total * 100) / 100
  }

  const total =
    selectImporteTotalProductos(cestaState) +
    selectImporteTotalServicios(cestaState) -
    selectGlobalDiscountAmount(cestaState) -
    selectMontajeMotoDiscount(cestaState)
  return Math.round(total * 100) / 100
}

export const isNullDiscount = (discount: CestaDiscount | Promocion) =>
  !discount.id_site_promocion_cesta

export const hasSeguroNeumatico = (cestaState: CestaState) => {
  const servicio = cestaState.services.find(
    (s) => s.id_navision === ID_NAVISION_SEGURO_NEUMATICO
  )
  return servicio && servicio.selected
}

export const hasRecogidaNeumatico = (cestaState: CestaState) => {
  const servicio = cestaState.services.find(
    (s) => s.id_navision === RECOGIDA_TALLER_ID
  )
  return servicio && servicio.selected
}

export const hasMontajeNeumatico = (cestaState: CestaState) => {
  const servicio = cestaState.services.find(
    (s) => s.id_navision === MONTAJE_TALLER_ID
  )
  return servicio && servicio.selected
}

export const hasMontajeBateria = (cestaState: CestaState) => {
  const servicio = cestaState.services.find(
    (s) => s.id_navision === MONTAJE_TALLER_BATERIA_ID
  )
  return servicio && servicio.selected
}

export const hasRecogidaAceite = (cestaState: CestaState) => {
  const servicio = cestaState.services.find(
    (s) => s.id_navision === RECOGIDA_TALLER_ACEITE_ID
  )
  return servicio && servicio.selected
}
export const canCestaMontaje = (products: Product[]) => {
  return (
    products.some((product) => product.tipo_producto === 'neumatico') &&
    products
      .filter((p) => p.tipo_producto === 'neumatico')
      .every((product) =>
        product.servicios.find(
          (service) => service.categoriaServicio === CategoriaServicio.Montaje
        )
      )
  )
}

export const calculatePriceWithoutDiscount = (service: Service) => {
  return calculateImporte(
    {
      precio: service.precio,
      cantidad: '1',
      descuento: '0.00',
      promo: '0.00',
      impuesto: service.impuesto,
    },
    {
      ecotasa: false,
      promo: false,
      impuesto: true,
    }
  )
}
export const hasMousseProduct = (products: Product[]) => {
  return products.some((p) => p.tipo_producto === 'mousse')
}

export const hasITV_APPLUS = (cestaState: CestaState) => {
  const servicio = cestaState.services.find(
    (s) => s.id_navision === ITV_APPLUS_ID
  )
  return servicio && servicio.selected
}

export const hasCestaNeumnaticos = (products: Product[]) =>
  products.some(p => p.tipo_producto == 'neumatico' && p.categoria == 'coche')
export const hasCestaRevision = (products: Product[]) =>
  products.some(p => p.tipo_producto == 'revision' && p.categoria == 'coche')

export const getSpecialServices = (cestaState: CestaState) => {
  const { products } = cestaState
  const specialServices = []

  if (isCestaCoche(products)) {
    if (hasCestaNeumnaticos(products)){
      specialServices.push('neumatico')
    }
    if (hasCestaRevision(products)){
      specialServices.push('revision')
    }
    if(hasITV_APPLUS(cestaState)){
      specialServices.push('itv_ap')
    }
  }

  if (isCestaMoto(products)) {
    switch (true) {
      case hasRecogidaNeumatico(cestaState):
        specialServices.push('moto_recogida')
        break
      case isCestaRevisionMoto(products):
        specialServices.push('revision_moto')
        break
      case hasMontajeNeumatico(cestaState) && !hasMousseProduct(products):
        specialServices.push('moto_montaje')
        break
      case hasMontajeNeumatico(cestaState) && hasMousseProduct(products):
        specialServices.push('moto_montaje_mouse')
        break
      default:
        break
    }
  }
  if (isCestaBateria(products)) {
    specialServices.push('bateria')
  }
  if (isCestaAceite(products)) {
    specialServices.push('aceite_recogida')
  }
  if (isCestaAC2(products)) {
    specialServices.push('ac2')
  }
  if (isCestaAC1(products)) {
    isAmbiguosVehicle(cestaState) ? specialServices.push('ac2') 
                                  : specialServices.push('ac')
  }

  return specialServices.join('|')
}

export const isHighCapacityCarter = (cestaState: CestaState, item: Product) => {
  return (
    item.tipo_producto === 'revision' &&
    cestaState.newVehicleData &&
    cestaState.newVehicleData.capacidad_carter &&
    parseFloat(cestaState.newVehicleData.capacidad_carter) > 5
  )
}

export const hasAireAcondicionadoMessage = (
  cestaState: CestaState,
  item: Product | Service
) => {
  return (
    item.tipo_producto === 'ac' && isAmbiguosVehicle(cestaState)
  )
}

export const getPromocionFechaLimite = (cestaState: CestaState) => {
  const { products, services } = cestaState
  const productWithPromo = products.find(
    (p) =>
      p.promocion.id_site_promocion_cesta !== null ||
      p.promocionServicio?.id_site_promocion_cesta
  )
  if (productWithPromo?.promocion.id_site_promocion_cesta) {
    return productWithPromo.promocion.fecha_limite ?? null
  }
  if (productWithPromo?.promocionServicio?.id_site_promocion_cesta) {
    return productWithPromo.promocionServicio.fecha_limite ?? null
  }
  const serviceWithPromo = services.find(
    (s) => s.promocion && s.promocion.id_site_promocion_cesta !== null
  )
  return serviceWithPromo?.promocion.fecha_limite ?? null
}
export const isCestaExternal = (cestaState: CestaState) =>
  cestaState.origin === CestaOrigin.External
